import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Paper,
    CircularProgress,
    Grid,
    Card,
    useTheme,
    LinearProgress,
} from '@mui/material';
import { format } from 'date-fns';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import EmailIcon from '@mui/icons-material/Email';
import ReplyIcon from '@mui/icons-material/Reply';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExtensionIcon from '@mui/icons-material/Extension';  // Add this import
import { Button } from '@mui/material';  // Add this import if not already present

const StatCard = ({ title, value, total, icon }) => {
    const percentage = Math.round((value / total) * 100) || 0;
    const theme = useTheme();
    return (
        <Card
            sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                p: 2,  // Reduced padding
                height: 'auto',  // Reduce height
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box>
                    <Typography variant="h4" sx={{ color: theme.palette.blue[900], fontWeight: 600, mb: 0.5 }}>
                        {value}
                    </Typography>
                    <Typography sx={{ color: theme.palette.text.secondary }}>
                        {title}
                    </Typography>
                </Box>
                <Box sx={{
                    color: theme.palette.blue[700],
                    backgroundColor: theme.palette.blue[50],
                    borderRadius: 1,
                    p: 1,
                }}>
                    {icon}
                </Box>
            </Box>
            <Box sx={{ mt: 1 }}>
                <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                        backgroundColor: theme.palette.grey[100],
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: theme.palette.blue[600]
                        }
                    }}
                />
            </Box>
        </Card>
    );
};

const Dashboard = () => {
    const theme = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [automations, setAutomations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        responded: 0,
        pending: 0
    });
    const columns = [
        {
            field: 'srNo',
            headerName: 'No.',
            width: 10,
            renderCell: (params) => (
                <Typography>
                    {automations.indexOf(params.row) + 1}
                </Typography>
            ),
        },
        {
            field: 'recipientEmail',
            headerName: 'Recruiter Email',
            width: 250,
            renderCell: (params) => (
                <Typography
                    sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    }}
                    onClick={() => navigate(`/email-automation/${params.row.id}`)}
                >
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'initialEmailSentDate',
            headerName: 'Initial Email Date',
            width: 200,
            valueFormatter: (params) =>
                format(new Date(params.value), 'MMM dd, yyyy HH:mm'),
        },
        {
            field: 'waitDays',
            headerName: 'Wait Period',
            width: 120,
            valueFormatter: (params) => `${params.value} days`,
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 100,
            renderCell: (params) => (
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        backgroundColor: getStatusColor(params.value),
                        color: 'white',
                    }}
                >
                    {params.value}
                </Box>
            ),
        },
        {
            field: 'hasReply',
            headerName: 'Reply Received',
            width: 120,
            renderCell: (params) => (
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        backgroundColor: params.value ? '#4caf50' : '#ff9800',
                        color: 'white',
                    }}
                >
                    {params.value ? 'Yes' : 'No'}
                </Box>
            ),
        },
        {
            field: 'followUpSentDate',
            headerName: 'Follow-up Sent',
            width: 200,
            valueFormatter: (params) =>
                params.value ? format(new Date(params.value), 'MMM dd, yyyy HH:mm') : 'Not sent yet',
        },
        {
            field: 'daysElapsed',
            headerName: 'Days Elapsed',
            width: 150,
            valueGetter: (params) => {
                const sentDate = new Date(params.row.initialEmailSentDate);
                const now = new Date();
                const diffTime = Math.abs(now - sentDate);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                return diffDays;
            },
            valueFormatter: (params) => `${params.value} days`,
        },
        {
            field: 'nextAction',
            headerName: 'Next Action',
            width: 200,
            valueGetter: (params) => {
                const sentDate = new Date(params.row.initialEmailSentDate);
                const waitDays = params.row.waitDays;
                const now = new Date();
                const diffTime = Math.abs(now - sentDate);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                if (params.row.hasReply) return 'None - Reply Received';
                if (params.row.followUpSentDate) return 'None - Follow-up Sent';
                if (diffDays >= waitDays) return 'Send Follow-up';
                return `Wait ${waitDays - diffDays} more days`;
            },
        }
    ];

    // Add this helper function
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return '#2196f3';
            case 'completed':
                return '#4caf50';
            case 'followup_sent':
                return '#ff9800';
            default:
                return '#757575';
        }
    };

    useEffect(() => {
        const fetchAutomations = async () => {
            if (!user?.email) return;

            try {
                const q = query(collection(db, 'emailAutomations'), where('userEmail', '==', user.email));
                const querySnapshot = await getDocs(q);

                const automationsData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                setAutomations(automationsData);

                // Updated stats calculation
                const statsData = {
                    total: automationsData.length,
                    active: automationsData.filter(a =>
                        a.status === 'active' && !a.hasReply && !a.followUpSentDate
                    ).length,
                    responded: automationsData.filter(a => a.hasReply).length,
                    pending: automationsData.filter(a =>
                        !a.hasReply &&
                        !a.followUpSentDate &&
                        a.status !== 'completed'
                    ).length,
                    completed: automationsData.filter(a =>
                        a.hasReply ||
                        a.status === 'completed' ||
                        a.followUpSentDate
                    ).length
                };
                setStats(statsData);

            } catch (error) {
                console.error('Error fetching automations:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAutomations();
    }, [user]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            backgroundColor: theme.palette.blue[900],
            pb: 4
        }}>
            <Container maxWidth="xl">
                <Box sx={{ pt: 4, pb: 4 }}>
                    <Grid container spacing={3} alignItems="center">
                        {/* Title Section */}
                        <Grid item xs={12} md={3}>
                            <Box>
                                <Typography
                                    variant="h4"
                                    color='white'
                                    sx={{
                                        color: 'rgba(255, 255, 255, 255)',
                                        fontWeight: 700,
                                        mb: 1
                                    }}
                                >
                                    apply-bridge
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography
                                        sx={{
                                            color: 'rgba(255, 255, 255, 255)',
                                        }}
                                    >
                                        Track and manage your network outreach
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<ExtensionIcon />}
                                        size="small"
                                        sx={{
                                            backgroundColor: theme.palette.blue[500],
                                            '&:hover': {
                                                backgroundColor: theme.palette.blue[600],
                                            },
                                            color: 'white',
                                            textTransform: 'none',
                                        }}
                                        onClick={() => window.open('https://chromewebstore.google.com/detail/applybridge/pehhplblehhcbehajdocbfcbgkemdknd', '_blank')}
                                    >
                                        Get Chrome Extension
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Stat Cards */}
                        <Grid item xs={12} md={9}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard
                                        title="Total Applications"
                                        value={stats.total}
                                        total={stats.total}
                                        icon={<EmailIcon />}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard
                                        title="Responses"
                                        value={stats.responded}
                                        total={stats.total}
                                        icon={<ReplyIcon />}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard
                                        title="Pending Follow-ups"
                                        value={stats.pending}
                                        total={stats.total}
                                        icon={<AccessTimeIcon />}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard
                                        title="Completed"
                                        value={stats.completed}
                                        total={stats.total}
                                        icon={<CheckCircleIcon />}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>

                <Paper
                    sx={{
                        borderRadius: 2,
                        backgroundColor: 'white',
                        overflow: 'hidden',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                >
                    <DataGrid
                        rows={automations}
                        columns={columns}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 8,
                                },
                            },
                        }}
                        pageSizeOptions={[8]}
                        disableSelectionOnClick
                        autoHeight
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-cell': {
                                borderBottomColor: theme.palette.grey[200],
                            },
                            '& .MuiDataGrid-columnHeaders': {
                                borderBottomColor: theme.palette.grey[200],
                                backgroundColor: theme.palette.grey[50],
                            },
                            '& .MuiDataGrid-footerContainer': {
                                borderTopColor: theme.palette.grey[200],
                                backgroundColor: theme.palette.grey[50],
                            },
                        }}
                    />
                </Paper>
            </Container>
        </Box>
    );
};

export default Dashboard;
